'use client'

import React from 'react'
import { TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import {
  collectPayment,
  createOrder,
  getLocalRegister,
  setCookieCartId,
} from '@/lib/actions'
import { useCheckout, useModals, useSearchParams } from '@/lib/hooks'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingDots,
} from '@/components/ui'

interface ConfirmNoWarrantyModalProps
  extends React.ComponentPropsWithoutRef<'div'> {
  open?: boolean
}

export const ConfirmNoWarrantyModal = ({
  open,
}: ConfirmNoWarrantyModalProps) => {
  const { closeModal, openModal } = useModals()
  const [isPending, startTransition] = React.useTransition()
  const { queryParams } = useCheckout()
  const { setSearchParams } = useSearchParams()

  const handleConfirm = () => {
    startTransition(async () => {
      try {
        const order = await createOrder({
          cart_id: Number(queryParams.cartId),
          customer_id: queryParams?.customerId,
        })
        await setCookieCartId('')
        setSearchParams({ orderId: order?.id })
        toast.success('Sale ended successfully')
        openModal('PRINT_RECEIPT')
      } catch {
        toast.error('Failed to end sale')
      } finally {
        closeModal('CONFIRM_NO_WARRANTY')
      }
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          closeModal('CONFIRM_NO_WARRANTY')
        }
      }}
    >
      <DialogContent
        closeOnOverlay
        onClose={() => closeModal('CONFIRM_NO_WARRANTY')}
        size="md"
      >
        <DialogHeader>
          <DialogTitle>
            <TriangleAlert
              className="size-10 text-yellow-500"
              strokeWidth={1.2}
            />
            <div className="flex items-center gap-2 pt-5">
              Products without warranty
            </div>
          </DialogTitle>
          <DialogDescription className="pt-2 text-left">
            You have products without warranty serial number. <br />
            Please confirm to end the sale.
          </DialogDescription>
          <DialogFooter className="flex w-full items-center gap-2 pt-5">
            <DialogClose asChild>
              <Button type="button" variant="secondary" className="w-full">
                Add Serial
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="brand"
              className="w-full"
              onClick={handleConfirm}
            >
              {isPending && <LoadingDots dotClassName="bg-white" />}
              {!isPending && <span>Skip Warranty</span>}
            </Button>
          </DialogFooter>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}
